<template>
  <!-- Pastille flottante quand l'espace est réduit -->
  <transition name="ws-fade">
    <button
      v-if="visible && collapsed"
      class="ws-pill"
      title="Rouvrir la fiche d'appel"
      @click="collapsed = false"
    >
      <span class="ws-pill-dot"></span>
      <span class="ws-pill-name">{{ entry?.name || entry?.phone }}</span>
      <Icon name="chevrons-right" :size="13" />
    </button>
  </transition>

  <!-- Espace de travail plein cadre (recouvre la zone de contenu) -->
  <transition name="ws-rise">
    <div v-if="visible && !collapsed" class="ws">
      <!-- ── Identité : le nom EST le formulaire ─────────────────────── -->
      <header class="ws-head">
        <span class="ws-avatar">{{ initials(form.name || entry?.phone || '?') }}</span>
        <div class="ws-id">
          <input
            v-model="form.name"
            class="ws-name"
            placeholder="Nom du contact…"
            maxlength="200"
            aria-label="Nom du contact"
          />
          <div class="ws-meta">
            <span class="ws-phone mono">{{ entry?.phone }}</span>
            <span class="ws-chip" :data-status="callStore.callStatus">
              <span class="ws-chip-dot"></span>
              {{ statusLabel }} <b v-if="duration">· {{ duration }}</b>
            </span>
            <span v-if="entry && entry.call_attempts > 0" class="ws-chip">
              {{ entry.call_attempts }} tentative{{ entry.call_attempts > 1 ? 's' : '' }}
            </span>
            <!-- Note Google + nombre d'avis (importés du CSV). -->
            <span
              v-if="googleRating"
              class="ws-chip ws-rating"
              :title="`Note Google${googleReviews ? ` · ${googleReviews} avis` : ''}`"
            >
              <Icon name="star" :size="11" class="ws-star" />
              {{ googleRating }}<b v-if="googleReviews"> · {{ googleReviews }} avis</b>
            </span>
            <!-- Ouvre la fiche Google Maps du contact (recherche nom + adresse). -->
            <a
              v-if="mapsUrl"
              :href="mapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="ws-chip ws-maps"
              title="Ouvrir dans Google Maps"
            >
              <Icon name="map-pin" :size="11" /> Maps
            </a>
          </div>
        </div>
        <div class="ws-head-actions">
          <button class="ws-tool" :class="{ on: showHistory }" title="Historique des modifications" @click="toggleHistory">
            <Icon name="clock" :size="14" />
            <span>Historique</span>
          </button>
          <button class="ws-tool" title="Réduire la fiche" @click="collapsed = true">
            <Icon name="chevrons-left" :size="14" />
            <span>Réduire</span>
          </button>
        </div>
      </header>

      <!-- ── Historique (traçabilité) ─────────────────────────────────── -->
      <transition name="ws-fade">
        <div v-if="showHistory" class="ws-history">
          <div v-if="historyLoading" class="muted small">Chargement…</div>
          <div v-else-if="!history.length" class="muted small">Aucune modification enregistrée sur cette fiche.</div>
          <ul v-else class="hist-list">
            <li v-for="(h, i) in history" :key="i" class="hist-item">
              <span class="hist-field">{{ fieldLabel(h.field) }}</span>
              <span class="hist-vals">
                <s v-if="h.old_value" class="hist-old">{{ h.old_value }}</s>
                <span v-else class="hist-old muted">vide</span>
                <Icon name="arrow-right" :size="11" class="hist-arrow" />
                <b>{{ h.new_value || 'vide' }}</b>
              </span>
              <span class="hist-by">{{ h.changed_by || 'système' }} · {{ sourceLabel(h.source) }}</span>
              <span class="hist-at mono">{{ fmtDateTime(h.changed_at) }}</span>
            </li>
          </ul>
        </div>
      </transition>

      <div class="ws-body">
        <!-- ── Colonne principale : formulaires ───────────────────────── -->
        <div class="ws-main">
          <!-- Fiche structurée -->
          <section class="ws-card">
            <h3 class="ws-card-title">Fiche contact</h3>
            <div class="ws-grid">
              <label class="f f-wide">
                <span class="f-lbl">Entreprise</span>
                <input v-model="form.company" placeholder="Nom de l'entreprise" maxlength="200" />
              </label>
              <label class="f">
                <span class="f-lbl">Email</span>
                <input v-model="form.email" type="email" placeholder="contact@exemple.fr" maxlength="254" />
              </label>
              <label class="f">
                <span class="f-lbl">Ville</span>
                <input v-model="form.city" placeholder="Lyon" maxlength="120" />
              </label>
              <label class="f">
                <span class="f-lbl">Secteur d'activité</span>
                <input v-model="form.business_sector" list="ws-sectors" placeholder="Restauration…" maxlength="150" />
                <datalist id="ws-sectors">
                  <option v-for="s in SECTORS" :key="s" :value="s" />
                </datalist>
              </label>
              <label class="f">
                <span class="f-lbl">Opérateur téléphonique</span>
                <input v-model="form.phone_operator" list="ws-operators" placeholder="Orange…" maxlength="80" />
                <datalist id="ws-operators">
                  <option v-for="o in OPERATORS" :key="o" :value="o" />
                </datalist>
              </label>
              <label class="f f-wide">
                <span class="f-lbl">Adresse</span>
                <input v-model="form.address" placeholder="12 rue des Lilas, 69003" maxlength="300" />
              </label>
            </div>
          </section>

          <!-- Champs personnalisés de la campagne -->
          <section v-if="callStore.customFields.length" class="ws-card">
            <h3 class="ws-card-title">Fiche campagne</h3>
            <div class="ws-grid">
              <label v-for="f in callStore.customFields" :key="f.id" class="f" :class="{ 'f-wide': f.field_type === 'textarea' }">
                <span class="f-lbl">
                  {{ f.label }}
                  <em v-if="!f.editable_by_agent" class="f-ro">lecture seule</em>
                  <em v-else-if="f.required" class="f-req">requis</em>
                </span>
                <template v-if="f.field_type === 'checkbox'">
                  <span class="f-check">
                    <input type="checkbox" v-model="callStore.fieldValues[f.key]" :disabled="!f.editable_by_agent" />
                    <span>{{ f.help_text || 'Oui' }}</span>
                  </span>
                </template>
                <textarea
                  v-else-if="f.field_type === 'textarea'"
                  v-model="callStore.fieldValues[f.key]"
                  rows="2"
                  :disabled="!f.editable_by_agent"
                  :placeholder="f.help_text"
                />
                <select v-else-if="f.field_type === 'select'" v-model="callStore.fieldValues[f.key]" :disabled="!f.editable_by_agent">
                  <option value="">—</option>
                  <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
                </select>
                <input
                  v-else
                  v-model="callStore.fieldValues[f.key]"
                  :type="inputType(f.field_type)"
                  :disabled="!f.editable_by_agent"
                  :placeholder="f.help_text"
                />
              </label>
            </div>
          </section>

          <!-- Champs personnalisés de la LISTE d'appartenance du numéro -->
          <section v-if="callStore.listFields.length" class="ws-card">
            <h3 class="ws-card-title">Fiche liste</h3>
            <div class="ws-grid">
              <label v-for="f in callStore.listFields" :key="'l' + f.id" class="f" :class="{ 'f-wide': f.field_type === 'textarea' }">
                <span class="f-lbl">
                  {{ f.label }}
                  <em v-if="!f.editable_by_agent" class="f-ro">lecture seule</em>
                  <em v-else-if="f.required" class="f-req">requis</em>
                </span>
                <template v-if="f.field_type === 'checkbox'">
                  <span class="f-check">
                    <input type="checkbox" v-model="callStore.fieldValues[f.key]" :disabled="!f.editable_by_agent" />
                    <span>{{ f.help_text || 'Oui' }}</span>
                  </span>
                </template>
                <textarea
                  v-else-if="f.field_type === 'textarea'"
                  v-model="callStore.fieldValues[f.key]"
                  rows="2"
                  :disabled="!f.editable_by_agent"
                  :placeholder="f.help_text"
                />
                <select v-else-if="f.field_type === 'select'" v-model="callStore.fieldValues[f.key]" :disabled="!f.editable_by_agent">
                  <option value="">—</option>
                  <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
                </select>
                <input
                  v-else
                  v-model="callStore.fieldValues[f.key]"
                  :type="inputType(f.field_type)"
                  :disabled="!f.editable_by_agent"
                  :placeholder="f.help_text"
                />
              </label>
            </div>
          </section>

          <!-- Rendez-vous -->
          <section class="ws-card">
            <h3 class="ws-card-title">Rendez-vous</h3>

            <!-- Formulaire RDV de la campagne : rempli pendant l'appel et
                 repris tel quel dans la fiche de fin d'appel (saisie
                 partagée, éditable jusqu'à la clôture). -->
            <template v-if="callStore.apptForm">
              <div class="ws-rdv-head">
                <div>
                  <div class="ws-rdv-q">Rendez-vous obtenu ?</div>
                  <div class="ws-rdv-name">{{ callStore.apptForm.name }}</div>
                </div>
                <ToggleSwitch v-model="callStore.apptObtained" />
              </div>
              <div v-if="callStore.apptObtained" class="ws-grid ws-rdv-grid">
                <label
                  v-for="f in callStore.apptForm.fields"
                  :key="f.id"
                  class="f"
                  :class="{ 'f-wide': f.field_type === 'textarea' }"
                >
                  <span class="f-lbl">
                    {{ f.label }}
                    <em v-if="f.required" class="f-req">requis</em>
                  </span>
                  <template v-if="f.field_type === 'checkbox'">
                    <span class="f-check">
                      <input type="checkbox" v-model="callStore.apptValues[f.key]" />
                      <span>{{ f.help_text || 'Oui' }}</span>
                    </span>
                  </template>
                  <textarea
                    v-else-if="f.field_type === 'textarea'"
                    v-model="callStore.apptValues[f.key]"
                    rows="2"
                    :placeholder="f.help_text"
                  />
                  <select v-else-if="f.field_type === 'select'" v-model="callStore.apptValues[f.key]">
                    <option value="">—</option>
                    <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
                  </select>
                  <input
                    v-else
                    v-model="callStore.apptValues[f.key]"
                    :type="inputType(f.field_type)"
                    :placeholder="f.help_text"
                  />
                </label>
              </div>
              <p class="f-hint">
                <Icon name="info" :size="11" />
                <template v-if="callStore.apptObtained">
                  Repris automatiquement dans la fiche de fin d'appel — modifiable jusqu'à la clôture.
                </template>
                <template v-else>Activez l'interrupteur dès qu'un rendez-vous est convenu.</template>
              </p>
            </template>

            <!-- Pas de formulaire rattaché à la campagne → simple mémo. -->
            <template v-else>
              <div class="ws-rdv">
                <label class="f">
                  <span class="f-lbl">Date</span>
                  <input v-model="rdvDate" type="date" :min="todayStr" />
                </label>
                <label class="f">
                  <span class="f-lbl">Heure</span>
                  <input v-model="rdvTime" type="time" />
                </label>
                <button v-if="rdvDate || rdvTime" class="ws-rdv-clear" title="Effacer le rendez-vous" @click="clearRdv">
                  <Icon name="x" :size="13" />
                </button>
              </div>
              <p class="f-hint">
                <Icon name="info" :size="11" />
                Renseignez date et heure si un rendez-vous est convenu.
              </p>
            </template>
          </section>

          <!-- Notes -->
          <section class="ws-card">
            <h3 class="ws-card-title">Commentaires & notes</h3>
            <textarea
              v-model="callStore.callNote"
              class="ws-notes"
              rows="3"
              placeholder="Notes pour cet appel — reprises automatiquement dans la fiche de fin d'appel…"
            />
          </section>
        </div>

        <!-- ── Colonne script ─────────────────────────────────────────── -->
        <aside class="ws-script">
          <div class="ws-script-head">
            <Icon name="book-open" :size="13" />
            <span>Script</span>
            <select v-if="scripts.length > 1" v-model="scriptIdx" class="ws-script-pick">
              <option v-for="(s, i) in scripts" :key="i" :value="i">{{ s.name }}</option>
            </select>
          </div>
          <pre v-if="resolvedScript" class="ws-script-body">{{ resolvedScript }}</pre>
          <p v-else class="muted small ws-script-empty">Aucun script pour cette campagne.</p>
        </aside>
      </div>

      <!-- ── Barre d'enregistrement ─────────────────────────────────────── -->
      <footer class="ws-foot">
        <span class="ws-saved" :class="{ dirty }">
          <template v-if="saving">Enregistrement…</template>
          <template v-else-if="dirty">Modifications non enregistrées</template>
          <template v-else-if="lastSavedAt">
            <Icon name="check" :size="12" /> Fiche enregistrée à {{ lastSavedAt }}
          </template>
          <template v-else>Aucune modification</template>
        </span>
        <button class="ws-save" :disabled="!dirty || saving" @click="save()">
          <Icon name="check" :size="14" />
          <span>{{ saving ? 'Enregistrement…' : 'Enregistrer la fiche' }}</span>
        </button>
      </footer>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import Icon from '@/shared/components/Icon.vue'
import ToggleSwitch from '@/shared/components/ToggleSwitch.vue'
import { QueueAPI, CampaignFieldAPI, NumberListFieldAPI, type FieldChange, type QueueEntry } from '@/shared/api/queue'
import type { CallScript } from '@/shared/api/forms'
import { useActiveCallStore } from '@/shared/stores/active_call'
import { useAuthStore } from '@/shared/stores/auth'
import { useToasts } from '@/shared/stores/toasts'
import { substituteScriptVariables } from '@/shared/utils/script_variables'

const callStore = useActiveCallStore()
const auth = useAuthStore()
const toasts = useToasts()

const entry = computed<QueueEntry | null>(() => callStore.selectedEntry)
const visible = computed(() => !!entry.value && callStore.callStatus !== 'idle')
const collapsed = ref(false)

// ── Enrichissement Google (note + avis + lien Maps), si importé du CSV ──────
// On lit les clés structurées (nouveaux imports) ET, en repli, les colonnes
// brutes `extras` (listes importées AVANT cette feature) → marche sur tout.
function toNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const n = Number(String(v ?? '').replace(',', '.').replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? n : null
}
const googleRating = computed(() => {
  const cd = entry.value?.custom_data
  const r = toNum(cd?.rating ?? cd?.extras?.rating)
  return r !== null && r > 0 && r <= 5 ? r : null
})
const googleReviews = computed(() => {
  const cd = entry.value?.custom_data
  const n = toNum(cd?.reviews_count ?? cd?.extras?.userRatingsTotal ?? cd?.extras?.reviews)
  return n !== null && n > 0 ? Math.round(n) : null
})
/** Lien Google Maps construit depuis le nom + l'adresse (recherche). */
const mapsUrl = computed(() => {
  const e = entry.value
  if (!e) return ''
  const addr = e.address || e.custom_data?.address || ''
  const q = [e.name, addr].filter(Boolean).join(' ').trim()
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : ''
})

// ── Suggestions (saisie libre conservée) ────────────────────────────────
const OPERATORS = [
  'Orange', 'SFR', 'Bouygues', 'Free',
  'Maroc Telecom', 'Inwi', 'Orange Maroc',
  'Bell', 'Rogers', 'Vidéotron', 'Telus', 'Autre',
]
const SECTORS = [
  'Restauration', 'Commerce', 'Santé', 'Immobilier', 'Bâtiment',
  'Services', 'Industrie', 'Transport', 'Éducation', 'Beauté & bien-être',
  'Automobile', 'Juridique', 'Finance', 'Autre',
]

// ── Formulaire structuré ────────────────────────────────────────────────
const form = reactive({
  name: '', company: '', email: '', city: '', address: '',
  business_sector: '', phone_operator: '',
})
/** Snapshot après chargement/sauvegarde — base du calcul `dirty`. */
let baseline = ''

function snapshot(): string {
  return JSON.stringify([form, callStore.fieldValues])
}
const dirtyTick = ref(0)
const dirty = computed(() => {
  void dirtyTick.value
  return snapshot() !== baseline
})
watch([form, () => callStore.fieldValues], () => { dirtyTick.value++ }, { deep: true })

function resetFromEntry(e: QueueEntry) {
  form.name = e.name || ''
  form.company = e.company || ''
  form.email = e.email || ''
  form.city = e.city || ''
  form.address = e.address || ''
  form.business_sector = e.business_sector || ''
  form.phone_operator = e.phone_operator || ''
}

async function loadCampaignFields(e: QueueEntry) {
  callStore.customFields = []
  callStore.listFields = []
  callStore.fieldValues = {}
  try {
    // Champs de la CAMPAGNE + champs de la LISTE d'appartenance du numéro.
    const [campFields, listFields] = await Promise.all([
      e.campaign ? CampaignFieldAPI.list(e.campaign) : Promise.resolve([]),
      e.number_list ? NumberListFieldAPI.list(e.number_list) : Promise.resolve([]),
    ])
    callStore.customFields = campFields.filter((f) => f.visible_to_agent || f.editable_by_agent)
    callStore.listFields = listFields.filter((f) => f.visible_to_agent || f.editable_by_agent)
    const existing = e.custom_data ?? {}
    const init: Record<string, any> = {}
    for (const f of [...callStore.customFields, ...callStore.listFields]) {
      if (init[f.key] === undefined) {
        init[f.key] = existing[f.key] ?? (f.field_type === 'checkbox' ? false : '')
      }
    }
    callStore.fieldValues = init
  } catch {
    callStore.customFields = []
    callStore.listFields = []
  }
}

// Formulaire RDV rattaché à la campagne — chargé dans le store partagé :
// l'agent le remplit pendant l'appel, la fiche de fin d'appel reprend la
// même saisie (validation des champs requis comprise).
async function loadAppointmentForm(e: QueueEntry) {
  callStore.apptForm = null
  callStore.apptObtained = false
  callStore.apptValues = {}
  try {
    const res = await QueueAPI.appointmentForm(e.id)
    callStore.apptForm = res.form
    if (res.form) {
      const init: Record<string, any> = {}
      for (const f of res.form.fields) init[f.key] = f.field_type === 'checkbox' ? false : ''
      callStore.apptValues = init
    }
  } catch {
    callStore.apptForm = null
  }
}

// ── Scripts ─────────────────────────────────────────────────────────────
const scripts = ref<CallScript[]>([])
const scriptIdx = ref(0)
const resolvedScript = computed(() => {
  const s = scripts.value[scriptIdx.value]
  if (!s?.content) return ''
  return substituteScriptVariables(s.content, {
    entry: entry.value,
    agent: {
      fullName: auth.displayName,
      firstName: auth.user?.first_name,
      teamName: auth.user?.team_name,
    },
  })
})

async function loadScripts(e: QueueEntry) {
  scripts.value = []
  scriptIdx.value = 0
  try {
    const r = await QueueAPI.scripts(e.id)
    scripts.value = r.scripts || []
  } catch {
    scripts.value = []
  }
}

// ── Rendez-vous ─────────────────────────────────────────────────────────
const rdvDate = ref('')
const rdvTime = ref('')
const todayStr = new Date().toISOString().slice(0, 10)

watch([rdvDate, rdvTime], ([d, t]) => {
  // Heure murale NAÏVE (sans fuseau) — localisée côté serveur (campagne › agent
  // › global) à la clôture, comme le formulaire RDV. Date seule → minuit ;
  // sans date on n'a pas de rendez-vous exploitable.
  callStore.plannedAppointmentAt = d ? (t ? `${d}T${t}` : d) : null
})
function clearRdv() {
  rdvDate.value = ''
  rdvTime.value = ''
}

// ── Historique (traçabilité) ────────────────────────────────────────────
const showHistory = ref(false)
const history = ref<FieldChange[]>([])
const historyLoading = ref(false)

const FIELD_LABELS: Record<string, string> = {
  name: 'Nom', company: 'Entreprise', email: 'Email', city: 'Ville', address: 'Adresse',
  business_sector: "Secteur d'activité", phone_operator: 'Opérateur',
}
function fieldLabel(f: string): string {
  if (f.startsWith('custom.')) {
    const key = f.slice('custom.'.length)
    return callStore.customFields.find((c) => c.key === key)?.label || key
  }
  return FIELD_LABELS[f] || f
}
const SOURCE_LABELS: Record<string, string> = {
  in_call: 'en appel', admin: 'superviseur', import: 'import', system: 'système',
}
function sourceLabel(s: string): string { return SOURCE_LABELS[s] || s }

async function toggleHistory() {
  showHistory.value = !showHistory.value
  if (!showHistory.value || !entry.value) return
  historyLoading.value = true
  try {
    const r = await QueueAPI.history(entry.value.id)
    history.value = r.changes
  } catch {
    history.value = []
  } finally {
    historyLoading.value = false
  }
}

// ── Sauvegarde ──────────────────────────────────────────────────────────
const saving = ref(false)
const lastSavedAt = ref('')

async function save(silent = false) {
  const e = entry.value
  if (!e || saving.value) return
  saving.value = true
  try {
    const editable: Record<string, any> = {}
    for (const f of [...callStore.customFields, ...callStore.listFields]) {
      if (f.editable_by_agent) editable[f.key] = callStore.fieldValues[f.key]
    }
    const res = await QueueAPI.updateContactCard(e.id, {
      ...form,
      custom_data: Object.keys(editable).length ? editable : undefined,
    })
    callStore.selectedEntry = res.entry
    baseline = snapshot()
    dirtyTick.value++
    lastSavedAt.value = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    if (!silent && res.changes > 0) {
      toasts.success(
        'Fiche enregistrée',
        `${res.changes} champ${res.changes > 1 ? 's' : ''} mis à jour (tracé).`,
      )
    }
    if (showHistory.value) {
      const r = await QueueAPI.history(e.id).catch(() => ({ changes: [] }))
      history.value = r.changes
    }
  } catch (err: any) {
    if (!silent) {
      toasts.error('Enregistrement impossible', err?.response?.data?.error || 'Réessayez.')
    }
  } finally {
    saving.value = false
  }
}

// ── Cycle de vie ────────────────────────────────────────────────────────
watch(
  () => entry.value?.id,
  async (id) => {
    if (!id || !entry.value) return
    collapsed.value = false
    showHistory.value = false
    history.value = []
    rdvDate.value = ''
    rdvTime.value = ''
    lastSavedAt.value = ''
    resetFromEntry(entry.value)
    // Marque l'entrée propriétaire de la fiche partagée — la fin d'appel
    // vérifie cette correspondance avant de reprendre les valeurs.
    callStore.ficheEntryId = id
    // Fiche FRAÎCHE depuis le serveur : l'objet transmis par les listes
    // (file, rappels, sélection) peut être périmé si la fiche a été éditée
    // lors d'un appel précédent — ces caches ne sont pas rafraîchis.
    try {
      const fresh = await QueueAPI.get(id)
      if (entry.value?.id === id) {
        callStore.selectedEntry = fresh
        resetFromEntry(fresh)
      }
    } catch { /* on garde l'objet transmis (hors-ligne, droits…) */ }
    if (!entry.value) return
    await Promise.all([
      loadCampaignFields(entry.value),
      loadAppointmentForm(entry.value),
      loadScripts(entry.value),
    ])
    baseline = snapshot()
    dirtyTick.value++
  },
  { immediate: true },
)

// Fin d'appel : sauvegarde silencieuse des saisies non enregistrées AVANT
// que la fiche de fin d'appel s'ouvre (aucune perte de données). On rouvre
// aussi l'espace s'il était réduit — l'agent garde la page pour finir sa
// saisie pendant la clôture.
watch(
  () => callStore.callStatus,
  (status) => {
    if (status !== 'ended') return
    collapsed.value = false
    if (dirty.value) void save(true)
  },
)

// ── Appels manuels (numéro composé hors file) ───────────────────────────
// Quand un appel démarre SANS fiche sélectionnée (composition manuelle ou
// appel entrant), on retrouve/crée la fiche du numéro côté serveur : le
// même espace de travail (formulaire, scripts, traçabilité, disposition)
// s'ouvre alors automatiquement. Les extensions internes (< 7 chiffres)
// sont ignorées — pas de fiche pour appeler un collègue.
let manualFetchFor = ''
watch(
  () => [callStore.callStatus, callStore.dialNumber] as const,
  async ([status, num]) => {
    const active = status === 'dialing' || status === 'ringing' || status === 'in_call'
    if (!active || callStore.selectedEntry) return
    const digits = (num || '').replace(/\D/g, '')
    if (digits.length < 7 || manualFetchFor === num) return
    manualFetchFor = num
    try {
      const res = await QueueAPI.manualCard(num)
      // L'appel peut s'être terminé / un autre numéro composé entre-temps.
      if (callStore.dialNumber === num && !callStore.selectedEntry) {
        callStore.selectedEntry = res.entry
      }
    } catch {
      /* fiche indisponible — le softphone reste utilisable sans espace */
    } finally {
      if (callStore.callStatus === 'idle') manualFetchFor = ''
    }
  },
)
// Réinitialise le garde-fou quand l'appel se termine.
watch(
  () => callStore.callStatus,
  (s) => { if (s === 'idle') manualFetchFor = '' },
)

// Chrono d'appel
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  timer = setInterval(() => { now.value = Date.now() }, 1000)
  // La fiche de fin d'appel déclenche ce flush avant d'enregistrer la
  // disposition : les édits faits après le raccroché ne sont pas perdus.
  callStore.flushFiche = async () => {
    if (dirty.value && !saving.value) await save(true)
  }
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
  callStore.flushFiche = null
})

const duration = computed(() => {
  if (!callStore.callAnsweredAt) return ''
  const d = Math.max(0, now.value - new Date(callStore.callAnsweredAt).getTime())
  const m = Math.floor(d / 60000)
  const s = Math.floor((d % 60000) / 1000)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

const statusLabel = computed(() => ({
  idle: '', dialing: 'Connexion…', ringing: 'Sonne',
  in_call: 'En appel', on_hold: 'En attente', ended: 'Terminé',
}[callStore.callStatus] || ''))

function initials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?'
}
function inputType(t: string): string {
  switch (t) {
    case 'number': return 'number'
    case 'date': return 'date'
    case 'time': return 'time'
    case 'datetime': return 'datetime-local'
    case 'email': return 'email'
    case 'phone': return 'tel'
    default: return 'text'
  }
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}
</script>

<style>
/* La zone de contenu sert de cadre de positionnement à l'espace d'appel. */
main.content { position: relative; }
</style>

<style scoped>
/* ── Pastille réduite ─────────────────────────────────────────────────── */
.ws-pill {
  position: absolute; left: 16px; bottom: 16px; z-index: 60;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 14px;
  background: var(--text, #111827); color: white;
  border: none; border-radius: var(--radius-full);
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
}
.ws-pill-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--success); animation: ws-pulse 1.4s infinite; }
.ws-pill-name { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@keyframes ws-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }

/* ── Cadre principal ──────────────────────────────────────────────────── */
.ws {
  position: absolute; inset: 0; z-index: 50;
  display: flex; flex-direction: column;
  background: var(--bg);
  overflow: hidden;
}

/* En-tête identité */
.ws-head {
  display: flex; align-items: center; gap: 16px;
  padding: 18px 24px 14px;
  background:
    radial-gradient(ellipse 600px 200px at 12% 0%, var(--primary-50) 0%, transparent 70%),
    var(--surface);
  border-bottom: 1px solid var(--border);
}
.ws-avatar {
  width: 56px; height: 56px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: var(--primary-100); color: var(--primary);
  border-radius: 50%;
  font-size: 19px; font-weight: 800;
  box-shadow: inset 0 0 0 2px var(--primary-200);
}
.ws-id { flex: 1; min-width: 0; }
.ws-name {
  width: 100%;
  border: none; background: transparent; outline: none;
  font-size: clamp(24px, 3.2vw, 34px);
  font-weight: 800; letter-spacing: -0.02em; color: var(--text);
  padding: 0; font-family: inherit;
  border-bottom: 2px dashed transparent;
  transition: border-color var(--duration-base);
}
.ws-name:hover { border-bottom-color: var(--border-strong); }
.ws-name:focus { border-bottom-color: var(--primary); }
.ws-name::placeholder { color: var(--text-disabled); font-weight: 700; }

.ws-meta { display: flex; align-items: center; gap: 10px; margin-top: 4px; flex-wrap: wrap; }
.ws-phone { font-size: 15px; font-weight: 600; color: var(--text-secondary); letter-spacing: 0.03em; }
.ws-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px;
  background: var(--surface-hover); color: var(--text-secondary);
  border-radius: var(--radius-full);
  font-size: 11.5px; font-weight: 600;
}
.ws-chip b { font-variant-numeric: tabular-nums; }
.ws-rating { background: #FEF3C7; color: #92400E; }
.ws-rating .ws-star { color: #F59E0B; fill: #F59E0B; }
.ws-maps { text-decoration: none; cursor: pointer; }
.ws-maps:hover { background: var(--primary-100); color: var(--primary); }
.ws-chip-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--text-muted); }
.ws-chip[data-status='in_call'] { background: var(--success-bg); color: var(--success); }
.ws-chip[data-status='in_call'] .ws-chip-dot { background: var(--success); animation: ws-pulse 1.4s infinite; }
.ws-chip[data-status='dialing'], .ws-chip[data-status='ringing'] { background: var(--warning-bg); color: var(--warning); }
.ws-chip[data-status='dialing'] .ws-chip-dot, .ws-chip[data-status='ringing'] .ws-chip-dot { background: var(--warning); }
.ws-chip[data-status='on_hold'] { background: var(--primary-100); color: var(--primary); }

.ws-head-actions { display: flex; gap: 6px; align-self: flex-start; }
.ws-tool {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 11px;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 12px; font-weight: 600; color: var(--text-secondary); cursor: pointer;
  transition: all var(--duration-base);
}
.ws-tool:hover { border-color: var(--border-strong); background: var(--surface-hover); }
.ws-tool.on { border-color: var(--primary); color: var(--primary); background: var(--primary-50); }

/* ── Historique ───────────────────────────────────────────────────────── */
.ws-history {
  max-height: 180px; overflow-y: auto;
  padding: 10px 24px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.hist-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.hist-item {
  display: grid; grid-template-columns: 130px 1fr 160px 90px;
  gap: 10px; align-items: baseline;
  font-size: 12px;
}
.hist-field { font-weight: 700; color: var(--text); }
.hist-vals { color: var(--text-secondary); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hist-old { color: var(--text-muted); }
.hist-arrow { color: var(--text-disabled); margin: 0 4px; vertical-align: middle; }
.hist-by { color: var(--text-muted); text-align: right; }
.hist-at { color: var(--text-muted); text-align: right; font-size: 11px; }

/* ── Corps : 2 colonnes ──────────────────────────────────────────────── */
.ws-body {
  flex: 1; min-height: 0;
  display: grid; grid-template-columns: 1fr 340px;
  gap: 14px;
  padding: 14px 24px;
  overflow: hidden;
}
.ws-main {
  display: flex; flex-direction: column; gap: 12px;
  overflow-y: auto; padding-right: 4px; min-width: 0;
}

.ws-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 14px 16px;
}
.ws-card-title {
  margin: 0 0 12px;
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.06em;
}

.ws-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 14px; }
.f { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.f-wide { grid-column: 1 / -1; }
.f-lbl { font-size: 11.5px; font-weight: 600; color: var(--text-secondary); display: flex; align-items: baseline; gap: 6px; }
.f-ro { font-style: normal; font-size: 10px; color: var(--text-muted); font-weight: 500; }
.f input, .f select, .f textarea {
  padding: 9px 11px;
  background: var(--bg);
  border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 13.5px; font-family: inherit; color: var(--text);
  outline: none;
  transition: border-color var(--duration-base), box-shadow var(--duration-base), background var(--duration-base);
}
.f input:focus, .f select:focus, .f textarea:focus {
  border-color: var(--primary); background: var(--surface);
  box-shadow: 0 0 0 3px var(--primary-100);
}
.f input:disabled, .f select:disabled, .f textarea:disabled { opacity: 0.6; cursor: not-allowed; }
.f-check { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 9px 0; }
.f-hint {
  display: inline-flex; align-items: center; gap: 5px;
  margin: 8px 0 0; font-size: 11.5px; color: var(--text-muted);
}

.ws-rdv { display: flex; align-items: flex-end; gap: 12px; }
.ws-rdv .f { flex: 0 0 170px; }
.ws-rdv-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ws-rdv-q { font-size: 13.5px; font-weight: 700; color: var(--text); }
.ws-rdv-name { font-size: 11.5px; color: var(--text-muted); margin-top: 1px; }
.ws-rdv-grid { margin-top: 12px; }
.f-req { font-style: normal; font-size: 10px; color: var(--danger); font-weight: 600; }
.ws-rdv-clear {
  width: 30px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  color: var(--text-muted); cursor: pointer;
}
.ws-rdv-clear:hover { color: var(--danger); border-color: var(--danger); background: var(--danger-bg); }

.ws-notes {
  width: 100%; box-sizing: border-box;
  padding: 10px 12px;
  background: var(--bg);
  border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 13.5px; font-family: inherit; line-height: 1.55; color: var(--text);
  resize: vertical; outline: none;
}
.ws-notes:focus { border-color: var(--primary); background: var(--surface); box-shadow: 0 0 0 3px var(--primary-100); }

/* ── Script ───────────────────────────────────────────────────────────── */
.ws-script {
  display: flex; flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden; min-height: 0;
}
.ws-script-head {
  display: flex; align-items: center; gap: 7px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-hover);
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.06em;
}
.ws-script-pick {
  margin-left: auto;
  max-width: 170px;
  padding: 4px 7px;
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  background: var(--surface); font-size: 11.5px;
  text-transform: none; letter-spacing: 0; font-weight: 500;
}
.ws-script-body {
  flex: 1; margin: 0; padding: 14px 16px;
  overflow-y: auto;
  white-space: pre-wrap; word-break: break-word;
  font-family: inherit; font-size: 13.5px; line-height: 1.65; color: var(--text);
}
.ws-script-empty { padding: 14px 16px; }

/* ── Pied : enregistrement ───────────────────────────────────────────── */
.ws-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 14px;
  padding: 10px 24px;
  background: var(--surface);
  border-top: 1px solid var(--border);
}
.ws-saved {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 500; color: var(--success);
}
.ws-saved.dirty { color: var(--warning); }
.ws-save {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px;
  background: var(--primary); color: white;
  border: none; border-radius: var(--radius);
  font-size: 13px; font-weight: 700; cursor: pointer;
  transition: background var(--duration-base), opacity var(--duration-base);
}
.ws-save:hover:not(:disabled) { background: var(--primary-hover); }
.ws-save:disabled { opacity: 0.45; cursor: not-allowed; }

.muted { color: var(--text-muted); }
.small { font-size: 12px; }
.mono { font-family: var(--font-mono); }

/* ── Transitions ─────────────────────────────────────────────────────── */
.ws-rise-enter-active { transition: opacity 0.22s ease, transform 0.22s ease; }
.ws-rise-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.ws-rise-enter-from, .ws-rise-leave-to { opacity: 0; transform: translateY(14px); }
.ws-fade-enter-active, .ws-fade-leave-active { transition: opacity 0.18s ease; }
.ws-fade-enter-from, .ws-fade-leave-to { opacity: 0; }

@media (max-width: 1100px) {
  .ws-body { grid-template-columns: 1fr; }
  .ws-script { display: none; }
}
</style>
